import { faBars, faFilter, faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Flex, Input, Modal, Table } from "antd";
import { useEffect, useMemo, useState } from "react";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import { PERMISSIONS } from "../../constants/permission";
import { useHasPermission } from "../../hooks/useHasPermission";
import { ISupplierResult, ISuppliersResponse } from "../../interfaces/supplier";
import { SupplierServices } from "../../services/supplier";
import { debounce } from "../../utils/helper";
import { getSupplierColumns } from "./components/columns";
import CustomizeModal from "./components/customize";
import { DEFAULT_COLUMN_CONFIGS } from "../../constants/default-customize";
import FilterSupplierDrawer from "./components/filter-supplier-drawer";
import { MASTERDATA_TYPE } from "../../constants/master-data";
import { Link } from "react-router-dom";
import { useTableView, useUpdateTableView } from "../../hooks/useTableView";
import { useMasterData } from "../../hooks/useMasterData";
import { useActiveSupplierFilterCount, useChangeSupplierStatus } from "../../hooks/useSupplier";
import { useSupplierContext } from "../../context/SupplierContext";

interface SortInfo {
  orderBy?: string;
  orderDirection?: "asc" | "desc";
}

export type SupplierFilterValues = {
  isActive?: "true" | "false";
  isCreditSupplier?: "true" | "false";
  apOwners?: string[];
  productOwners?: string[];
  isPartOfGroup?: "true" | "false";
  groupName?: string;
  countrys?: string[];
  currencys?: string[];
  nominalAccountDebits?: string[];
  nominalAccountCredits?: string[];
};

const SupplierPage: React.FC = () => {
  const { setValue, getValue, clearAll } = useSupplierContext();

  const [suppliers, setSuppliers] = useState<ISupplierResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");
  const [sortInfo, setSortInfo] = useState<SortInfo>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });
  const hasPermissionEditor = useHasPermission([PERMISSIONS.SUPPLIER_EDITOR]);
  const [showModal, setShowModal] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [isFetchedMaterData, setIsFetchedMaterData] = useState(false);
  const types = useMemo(
    () => [
      MASTERDATA_TYPE.COUNTRY,
      MASTERDATA_TYPE.CURRENCY,
      MASTERDATA_TYPE.NOMINAL_ACCOUNT_CREDIT,
      MASTERDATA_TYPE.NOMINAL_ACCOUNT_DEBIT,
    ],
    []
  );

  const { data: masterData } = useMasterData({
    types,
    includeUsers: true,
    enabled: isFetchedMaterData
  });

  const {
    columns: columnConfigs,
    setColumns: setColumnConfigs,
    filters,
    setFilters,
    loading: loadingTableView,
  } = useTableView({
    columnDefault: DEFAULT_COLUMN_CONFIGS,
    filterDefault: {},
    tableName: "suppliers",
  });
  const activeFilterCount = useActiveSupplierFilterCount(filters);
  const { updateTableView } = useUpdateTableView();

  const {
    isLoading: isLoadingChangeStatus,
    selectSupplier,
    showModal: showModalChangeStatus,
    closeModal: handleCancelChangeStatus,
    handleChangeStatus,
    isModalOpenChangeStatus,
  } = useChangeSupplierStatus(() => {
    fetchData();
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response: ISuppliersResponse = await SupplierServices.getAll({
        keyword: debouncedSearchValue,
        ...filters,
        page: pagination.current,
        pageSize: pagination.pageSize,
        orderBy: sortInfo.orderBy,
        orderDirection: sortInfo.orderDirection,
      });
      setSuppliers(response.data.results);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.totalElements || 0,
      }));
    } catch (error) {
      console.error("Fetch suppliers error:", error);
    } finally {
      setLoading(false);
      setIsFetchedMaterData(true)
    }
  };

  useEffect(() => {
    if (!loadingTableView) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTableView, debouncedSearchValue, filters, pagination.current, pagination.pageSize, sortInfo]);

  useEffect(() => {
    // 1) search / pagination
    const search = getValue<string>("search") ?? "";
    const page = getValue<number>("page") ?? 1;
    const pageSize = getValue<number>("pageSize") ?? DEFAULT_PAGINATION.pageSize;

    setSearchValue(search);
    setDebouncedSearchValue(search);
    setPagination(prev => ({ ...prev, current: page, pageSize: pageSize }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setValue("search", value);
        setDebouncedSearchValue(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
        setValue("page", 1);
        setValue("pageSize", pagination.pageSize);
      }, 1000),
    []
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    const prevPageSize = pagination.pageSize; // from your state
    const nextPageSize = paginationConfig.pageSize ?? prevPageSize;
    const nextPage =
      nextPageSize !== prevPageSize ? 1 : (paginationConfig.current ?? pagination.current);

    setPagination(prev => ({
      ...prev,
      current: nextPage,
      pageSize: nextPageSize,
    }));

    setValue("page", nextPage);
    setValue("pageSize", nextPageSize);

    // Handle sorting
    if (sorter?.field) {
      setSortInfo({
        orderBy: sorter.field,
        orderDirection: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      // Clear sorting
      setSortInfo({});
    }
  };

  const handleApplyFilters = (values: SupplierFilterValues) => {
    const newFilters = { ...filters, ...values };
    setFilters(newFilters);
    updateTableView({
      tableName: "suppliers",
      filters: newFilters,
    });
    clearAll();
    setDebouncedSearchValue("");
    setSearchValue("");
    setOpenFilter(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const renderModalChangeStatus = () => (
    <Modal
      title={`Confirm ${selectSupplier?.isActive ? "Disable" : "Enable"}`}
      closable={{ "aria-label": "Custom Close Button" }}
      open={isModalOpenChangeStatus}
      onOk={handleChangeStatus}
      onCancel={handleCancelChangeStatus}
      okText="Yes"
      confirmLoading={isLoadingChangeStatus}
    >
      <p style={{ fontSize: "20px" }}>
        Are you sure you want to {selectSupplier?.isActive ? "disable" : "enable"} supplier{" "}
        <span className="text-primary font-italic">{selectSupplier?.supplierName}</span>?
      </p>
    </Modal>
  );

  const columns = useMemo(() => {
    return getSupplierColumns(hasPermissionEditor, columnConfigs, showModalChangeStatus);
  }, [hasPermissionEditor, columnConfigs, showModalChangeStatus]);

  return (
    <div className="page-supplier">
      <Flex gap={16} justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <h2 className="title-page">Suppliers</h2>
        {hasPermissionEditor && (
          <Link to={`/supplier/create`}>
            <Button type="primary">
              <FontAwesomeIcon icon={faPlus} /> Create
            </Button>
          </Link>
        )}
      </Flex>
      <div
        style={{
          borderBottom: "1px solid #e8e8e8",
          marginBottom: 16,
        }}
      ></div>

      <Flex gap={16} justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Input
          placeholder="Enter Supplier Code or Supplier Name"
          value={searchValue}
          onChange={handleInputChange}
          prefix={<FontAwesomeIcon icon={faSearch} style={{ color: "#999" }} />}
          style={{ width: 400 }}
          size="large"
          allowClear
        />
        <Flex gap={12}>
          <Button color="primary" variant="outlined" onClick={() => setShowModal(true)}>
            <FontAwesomeIcon icon={faBars} style={{ fontSize: "16px" }} />
            Customise
          </Button>
          <Button color="primary" variant="outlined" onClick={() => setOpenFilter(true)}>
            <FontAwesomeIcon icon={faFilter} style={{ fontSize: "16px" }} />
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </Button>
        </Flex>
      </Flex>

      <Table
        dataSource={suppliers}
        loading={loading || loadingTableView}
        rowKey={(record) => record.id}
        columns={columns}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
        size="small"
        className="table-fm"
        rowClassName={(record, index) => {
          let className = index % 2 === 0 ? "highlight-row" : "";
          if (!record.isActive) {
            className += " disabled-row";
          }
          return className.trim();
        }}
        scroll={{ x: 1200 }}
      />
      {showModal && (
        <CustomizeModal
          open={showModal}
          onClose={() => setShowModal(false)}
          configsTemplate={DEFAULT_COLUMN_CONFIGS}
          configsValue={columnConfigs}
          onSave={(newConfigs) => setColumnConfigs(newConfigs)}
        />
      )}
      {openFilter && (
        <FilterSupplierDrawer
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          onApply={handleApplyFilters}
          masterData={masterData}
          defaultValues={filters}
        />
      )}
      {renderModalChangeStatus()}
    </div>
  );
};

export default SupplierPage;
